import { BasemapInk } from '~/client/src/shared/map/labelLayerStyle'
import { liveCoordinates } from '~/client/src/shared/utils/liveEquipmentPosition'

import ThemeMode from '../../../utils/ThemeModeManager'
import { IPlateDecision } from '../../MapBoxViewer/labelPlateDecision'
import { mapViewItemKey } from '../../MapBoxViewer/mapboxConstants'
import MapViewItemBase from '../models/MapViewItemBase'
import PolyLineShapeCoordinates, {
  isBeyondOneRingShape,
} from '../models/PolyLineShapeCoordinates'
import { getMarkerImageKey, getSortKey } from './GLMarkerImageGenerator'
import {
  ILocationClosureContext,
  isLocationClosed,
  isLocationPartiallyClosed,
} from './locationClosureState'
import { IPlanFootprint, isOnPlanSheet } from './planFootprints'

// Whose colour a label's text is drawn in, when it is drawn in a colour at all.
//
// A text sticker keeps the colour its own picker set. Nothing else has one — labelProperties
// .color is reachable from no UI, so a location carries the constructor's default forever
// whatever colour its marker is. Following item.color instead makes a plated label and its
// circle agree, which is what they already look like where no stale value was persisted.
function labelColorOf(item: MapViewItemBase): string {
  const labelProperties = item.globeViewItemProperties.labelProperties
  return ThemeMode.getHEXColor(
    item.isRichTextBox ? labelProperties?.color || item.color : item.color,
  )
}

const OUTLINED_SORT_KEY = -1

export function buildLocationMarkerGeoJson(
  items: MapViewItemBase[],
  closureContext?: ILocationClosureContext,
  // Ink for the labels that adapt to their ground. `tones` is absent per label for anything
  // the reader could not place, and absent entirely for a host with no ground context, which
  // leaves every unplated label on the basemap guess.
  ink?: { tones?: Map<string, IPlateDecision>; basemap: BasemapInk },
  // Where the plan sheets are. A label standing on one is plated even with its box switched
  // off — see the comment on `platedByPlan` below.
  planFootprints?: IPlanFootprint[],
  // The item the editor is working on, drawn with a ring and NOTHING else changed. Deliberately
  // not `selected`: that name already drives the label paint's emphasis branch, so reusing it
  // would restyle the very label the editor is previewing.
  outlinedItemId?: string,
  // No ring while the Shape part is edited: the marker is not what is being edited.
  shouldHideOutline?: boolean,
  // The location whose card is open: it paints from the selected tier, above the pins.
  liftedItemId?: string,
  // The edited item when the view hides it: drawn so it can be edited, marked so it is not
  // mistaken for one the view shows.
  offViewItemId?: string,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: items.flatMap(item => {
      const labelProperties = item.globeViewItemProperties.labelProperties
      const labelColor = labelColorOf(item)
      const onPlan =
        !!planFootprints?.length &&
        isOnPlanSheet(
          item.sitemapItem.coordinates.longitude,
          item.sitemapItem.coordinates.latitude,
          planFootprints,
        )
      const labelSize = labelProperties?.fontSize || 12
      const read = ink?.tones?.get(mapViewItemKey(item.id))
      const isEdited =
        !!outlinedItemId && mapViewItemKey(item.id) === outlinedItemId
      const isOffView =
        !!offViewItemId && mapViewItemKey(item.id) === offViewItemId
      const imageState = {
        iconType: item.iconName,
        color: ThemeMode.getHEXColor(item.color),
        isClosed:
          !!closureContext && isLocationClosed(item.dataObject, closureContext),
        hasClosures:
          !!closureContext &&
          isLocationPartiallyClosed(item.dataObject, closureContext),
        isLive: !!liveCoordinates(item.dataObject),
      }

      const feature = {
        type: 'Feature' as const,
        properties: {
          itemId: mapViewItemKey(item.id),
          name: item.displayName,
          iconImage: getMarkerImageKey(imageState),
          // Carried next to the base image so hover swaps images through a layer
          // expression instead of re-emitting the whole source.
          iconImageHighlighted: getMarkerImageKey({
            ...imageState,
            isHighlighted: true,
          }),
          showIcon:
            item.globeViewItemProperties.iconProperties?.isDisplayed ?? false,
          showLabel: labelProperties?.isDisplayed ?? false,
          // A plated label cannot share a symbol with the circle — a symbol carries one
          // icon-image and the plate has to BE it — so plated items split across layers.
          //
          // Two reasons to plate, and the second OVERRIDES the editor: the checkbox is a
          // preference, but ground unusable for bare type is a legibility failure a preference
          // cannot answer. Read from the ground rather than from whether a plan is present, so
          // the same drawing behaves the same way as a plan or a tileset; until it has been read,
          // fall back to the footprint. Strictly additive — never removes a plate asked for.
          boxedLabel:
            !!labelProperties?.isTextBoxDisplayed || (read?.plated ?? onPlan),
          platedByPlan:
            (read?.plated ?? onPlan) && !labelProperties?.isTextBoxDisplayed,
          isOutlined: isEdited && !shouldHideOutline,
          isLifted: !!liftedItemId && mapViewItemKey(item.id) === liftedItemId,
          isOffView,
          isDataLess: item.isDataLess,
          labelColor,
          // Only an unplated label reads this: a plated one keeps its colour, because the
          // plate is a light ground the colour is already legible on.
          tone: read?.tone ?? ink?.basemap ?? 'light',
          labelSize,
          // Placed first, so dragging the edited item over another never leaves it the
          // symbol collision evicts. Every other key is >= 1000.
          sortKey: isEdited
            ? OUTLINED_SORT_KEY
            : getSortKey(item.iconName) * 1000 +
              ((item.sitemapItem.coordinates.latitude + 90) / 180) * 999,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [
            item.sitemapItem.coordinates.longitude,
            item.sitemapItem.coordinates.latitude,
          ],
        },
      }

      // A foreman on any part of a multi-part zone can read which zone it is. Label only: the
      // pin stays one per location, and the editor's ring stays on the one feature it outlines.
      const shape = item.sitemapItem.shapeCoordinates
      const partLabels =
        feature.properties.showLabel &&
        item.globeViewItemProperties.shapeProperties?.isDisplayed &&
        isBeyondOneRingShape(shape)
          ? (shape as PolyLineShapeCoordinates).additionalPartAnchors.map(
              coordinates => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  showIcon: false,
                  isOutlined: false,
                },
                geometry: { type: 'Point' as const, coordinates },
              }),
            )
          : []

      return [feature, ...partLabels]
    }),
  }
}

export interface ILocationMarkerParts {
  markers: boolean
  labels: boolean
}

export function maskLocationMarkerParts(
  fc: GeoJSON.FeatureCollection<GeoJSON.Point>,
  parts: ILocationMarkerParts,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  if (parts.markers && parts.labels) {
    return fc
  }
  return {
    type: 'FeatureCollection',
    features: fc.features.flatMap(feature => {
      const showIcon = feature.properties.showIcon && parts.markers
      const showLabel = feature.properties.showLabel && parts.labels
      return showIcon || showLabel
        ? [
            {
              ...feature,
              properties: { ...feature.properties, showIcon, showLabel },
            },
          ]
        : []
    }),
  }
}

// Moves the icons named in `positions` (by itemId) to the given coordinates, leaving every other
// feature, and every label-only part, exactly where it was.
export function placeLocationMarkers(
  fc: GeoJSON.FeatureCollection<GeoJSON.Point>,
  positions: ReadonlyMap<string, [number, number]>,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  if (!positions.size) {
    return fc
  }
  return {
    type: 'FeatureCollection',
    features: fc.features.map(feature => {
      const position =
        feature.properties.showIcon && positions.get(feature.properties.itemId)
      return position
        ? {
            ...feature,
            geometry: { type: 'Point' as const, coordinates: position },
          }
        : feature
    }),
  }
}
